import React from "react";
import PaperWorkspace from "../paper/PaperWorkspace";
import { marketFetchJson } from "./marketApi";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Default symbols always available in the picker
const DEFAULT_SYMBOLS = [
  "BTC", "ETH", "SOL", "BNB", "XRP", "DOGE",
  "ADA", "LINK", "DOT", "AVAX", "LTC", "UNI",
];

function fmt(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1)    return n.toFixed(2);
  return n.toFixed(4);
}

export default function PositionChartCard({ positions: suppliedPositions }) {
  const [fetchedPositions, setFetchedPositions] = React.useState([]);
  const [feedError, setFeedError] = React.useState("");
  const [loading, setLoading] = React.useState(!Array.isArray(suppliedPositions));
  const [selected, setSelected]   = React.useState("BTC");
  const positions = Array.isArray(suppliedPositions) ? suppliedPositions : fetchedPositions;

  // Standalone fallback. The Markets workspace supplies its canonical snapshot
  // directly so chart overlays and position cards always use identical marks.
  React.useEffect(() => {
    if (Array.isArray(suppliedPositions)) {
      setLoading(false);
      setFeedError("");
      return undefined;
    }
    let live = true;
    const load = () =>
      marketFetchJson(`${API_URL}/api/positions`)
        .then(d => {
          if (!live) return;
          const list = Array.isArray(d.positions) ? d.positions : [];
          setFetchedPositions(list);
          setFeedError("");
          // Auto-select first position symbol if we don't already have one open
          if (list.length > 0) {
            const firstSym = list[0].symbol?.replace("/USDT", "");
            if (firstSym) setSelected(s => s === "BTC" ? firstSym : s);
          }
        })
        .catch((error) => {
          if (live) setFeedError(error?.message || "Position feed unavailable");
        })
        .finally(() => {
          if (live) setLoading(false);
        });
    load();
    const id = setInterval(load, 6000);
    return () => { live = false; clearInterval(id); };
  }, [suppliedPositions]);

  // Symbols that have an open position
  const positionSymbols = React.useMemo(() => {
    const set = new Set();
    for (const p of positions) {
      const s = p.symbol?.replace("/USDT", "");
      if (s) set.add(s);
    }
    return set;
  }, [positions]);

  // Full symbol list: positions first, then defaults (no dupes)
  const allSymbols = React.useMemo(() => {
    const withPos = [...positionSymbols];
    const rest = DEFAULT_SYMBOLS.filter(s => !positionSymbols.has(s));
    return [...withPos, ...rest];
  }, [positionSymbols]);

  // Build the position overlay for the current selected symbol
  const chartPositions = React.useMemo(() => {
    return positions
      .filter(p => p.symbol?.replace("/USDT", "") === selected)
      .map(p => ({
        symbol:            p.symbol,
        entry_price:       p.entry_price,
        stop_price:        p.stop_loss ?? p.stop_price,
        target_price:      p.take_profit ?? p.target_price,
        liquidation_price: p.liquidation_price,
        direction:         p.direction,
      }));
  }, [positions, selected]);

  // Find the position info to show in the badge (if any)
  const activeTrades = positions.filter(p => p.symbol?.replace("/USDT", "") === selected);

  return (
    <div className="trade-market-terminal">
      <header className="trade-market-terminal-head">
        <div className="trade-market-instrument">
          <span className="trade-market-live-dot"><i /></span>
          <div>
            <small>Live instrument</small>
            <strong>{selected}<span>/ USDT</span></strong>
          </div>
        </div>
        <div className="trade-market-terminal-state">
          <span>Position overlay</span>
          <strong className={activeTrades.length && !feedError ? "is-active" : ""}>
            {feedError
              ? "Position feed degraded"
              : loading
                ? "Syncing positions"
                : activeTrades.length
              ? `${activeTrades.length} active trade${activeTrades.length > 1 ? "s" : ""}`
              : "No open exposure"}
          </strong>
        </div>
      </header>

      <nav className="trade-symbol-rail" aria-label="Chart instrument">
        <span>Instruments</span>
        <div>
          {allSymbols.map(sym => {
            const hasPos  = positionSymbols.has(sym);
            const active  = selected === sym;
            return (
              <button
                key={sym}
                onClick={() => setSelected(sym)}
                className={`${active ? "is-active" : ""} ${hasPos ? "has-position" : ""}`}
              >
                {sym}
                {hasPos && <i />}
              </button>
            );
          })}
        </div>
      </nav>

      {activeTrades.length > 0 && (
        <div className="trade-chart-position-strip">
          {activeTrades.slice(0, 3).map((p, i) => {
            const isLong = p.direction === "LONG";
            const pnl    = p.unrealized_pnl ?? 0;
            const profit = pnl >= 0;
            return (
              <div key={i} className={isLong ? "is-long" : "is-short"}>
                <span className="trade-chart-direction">
                  {isLong ? "LONG" : "SHORT"} {p.leverage}x
                </span>
                <span><small>Entry</small><strong>${fmt(p.entry_price)}</strong></span>
                <span><small>Stop</small><strong className="is-stop">${fmt(p.stop_loss ?? p.stop_price)}</strong></span>
                <span><small>Target</small><strong className="is-target">${fmt(p.take_profit ?? p.target_price)}</strong></span>
                <span className={`trade-chart-position-pnl ${profit ? "is-positive" : "is-negative"}`}>
                  <small>Open P&amp;L</small>
                  <strong>
                  {profit ? "+" : ""}${fmt(pnl)}
                  </strong>
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="trade-chart-canvas">
        <PaperWorkspace
          symbol={selected}
          onSymbolChange={setSelected}
          chartPositions={chartPositions}
        />
      </div>
    </div>
  );
}
